import { memo, useCallback } from 'react';

import { ControllerRenderProps } from 'react-hook-form';

import { Image } from '@/components/shared/Image';
import { P } from '@/components/shared/Typography';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { T_ProfileFormData } from '@/lib/schemas/profileForm.schema';
import { E_ProfileForm } from '@/ts/enums/E_ProfileForm';
import { I_LanguageOption } from '@/ts/interfaces/I_LanguageOption';
import { T_Country } from '@/ts/types/T_Country';

interface I_ProfileInformationSelectProps {
  type: 'language' | 'country';
  field: ControllerRenderProps<
    T_ProfileFormData,
    E_ProfileForm.LANGUAGE | E_ProfileForm.COUNTRY
  >;
  options: T_Country[] | I_LanguageOption[];
  placeholder: string;
}

const ProfileInformationSelect = memo(function ProfileInformationSelect({
  type,
  field,
  options,
  placeholder
}: I_ProfileInformationSelectProps) {
  const getOptionValue = useCallback(
    (option: T_Country | I_LanguageOption) => {
      return type === 'language'
        ? (option as I_LanguageOption).code
        : (option as T_Country).alpha2Code;
    },
    [type]
  );

  const getOptionName = useCallback(
    (option: T_Country | I_LanguageOption) => {
      return type === 'language'
        ? (option as I_LanguageOption).name
        : (option as T_Country).nativeName;
    },
    [type]
  );

  const getOptionFlag = useCallback(
    (option: T_Country | I_LanguageOption) => {
      return type === 'language'
        ? (option as I_LanguageOption).flag
        : (option as T_Country).flag;
    },
    [type]
  );

  const handleValueChange = useCallback(
    (value: string) => {
      field.onChange(value);
    },
    [field]
  );

  return (
    <>
      <div className="w-full relative">
        <Select value={field.value} onValueChange={handleValueChange}>
          <SelectTrigger className="w-full bg-accordion-brand h-9 rounded-lg cursor-pointer">
            <SelectValue
              className="text-sm font-medium leading-tight"
              placeholder={placeholder}
            />
          </SelectTrigger>
          <SelectContent className="bg-accordion-brand text-foreground max-h-60.5">
            <SelectGroup>
              {options.map(option => (
                <SelectItem
                  key={getOptionValue(option)}
                  value={getOptionValue(option)}
                  className="data-[state=checked]:font-bold"
                >
                  <div className="flex items-center gap-2 max-w-76">
                    <div className="rounded-full relative flex items-center gap-2">
                      <Image
                        width={16}
                        height={16}
                        className="w-4 h-4 rounded-full object-cover object-center"
                        src={getOptionFlag(option)}
                        alt={getOptionName(option)}
                        unoptimized
                      />
                      <P className="flex">{getOptionName(option)}</P>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </>
  );
});

export default ProfileInformationSelect;
